import React, { useEffect } from 'react'
import { AlertTriangle, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
  Button,
  Badge,
} from '../ui'
import { useDeletePosition, type Position } from '../../hooks/usePositions'
import { LoadingSpinner } from '../ui/loading-states'

interface DeletePositionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  position: Position | null
}

export function DeletePositionDialog({
  open,
  onOpenChange,
  position
}: DeletePositionDialogProps) {
  const deletePosition = useDeletePosition()
  const isLoading = deletePosition.isPending

  // Сброс состояния ошибки при открытии/закрытии диалога
  useEffect(() => {
    if (!open && deletePosition.error) {
      deletePosition.reset()
    }
  }, [open, deletePosition])

  const handleDelete = async () => {
    if (!position) return

    try {
      await deletePosition.mutateAsync(position.id)
      // Закрываем диалог сразу после успешного удаления
      onOpenChange(false)
    } catch (error: any) {
      // Ошибка будет показана в UI через deletePosition.error
      // Диалог остается открытым чтобы показать ошибку
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      onOpenChange(false)
    }
  }

  if (!position) return null

  const employeeCount = position.employees?.length || 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <DialogTitle>
                <span className="text-red-900">Удалить должность?</span>
              </DialogTitle>
              <DialogDescription>
                Это действие нельзя будет отменить.
              </DialogDescription>
            </div>
          </div>
          {!isLoading && <DialogClose onClose={handleClose} />}
        </DialogHeader>

        <div className="py-4">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="space-y-2">
              <div>
                <span className="text-sm font-medium text-gray-700">Название:</span>
                <p className="text-sm text-gray-900">{position.name}</p>
              </div>

              {position.departments && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Подразделение:</span>
                  <p className="text-sm text-gray-900">{position.departments.name}</p>
                </div>
              )}

              {position.code_1c && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Код 1С:</span>
                  <p className="text-sm text-gray-900">{position.code_1c}</p>
                </div>
              )}


              <div>
                <span className="text-sm font-medium text-gray-700">Источник:</span>
                <div className="mt-1">
                  {position.code_1c ? (
                    <Badge variant="secondary">1С</Badge>
                  ) : (
                    <Badge variant="outline">TCO</Badge>
                  )}
                </div>
              </div>

              {employeeCount > 0 && (
                <div>
                  <span className="text-sm font-medium text-gray-700">Сотрудники:</span>
                  <div className="flex items-center mt-1">
                    <Users className="h-4 w-4 text-gray-500 mr-1" />
                    <span className="text-sm text-gray-900">{employeeCount} сотрудник(ов)</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Warning about consequences */}
          {employeeCount > 0 ? (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-red-800">
                  <p className="font-medium">Удаление невозможно!</p>
                  <p className="mt-1">
                    К этой должности привязано {employeeCount} сотрудник(ов).
                  </p>
                  <p className="mt-1">
                    Для удаления должности сначала необходимо:
                  </p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Переназначить всех сотрудников на другие должности</li>
                    <li>Или удалить записи сотрудников</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <div className="flex items-start">
                <AlertTriangle className="h-4 w-4 text-yellow-600 mt-0.5 mr-2 flex-shrink-0" />
                <div className="text-sm text-yellow-800">
                  <p className="font-medium">Внимание!</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Должность будет удалена безвозвратно</li>
                    <li>Все связанные данные будут утеряны</li>
                    <li>Это действие нельзя отменить</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Show API errors */}
          {deletePosition.error && (
            <div className="mt-4 flex items-center p-3 text-sm text-red-700 bg-red-100 rounded-md">
              <AlertTriangle className="h-4 w-4 mr-2 flex-shrink-0" />
              <span>
                {(() => {
                  const error = deletePosition.error as any
                  // API клиент возвращает ошибку с полями message и status напрямую
                  const message = error?.message
                  const status = error?.status

                  if (message === 'Cannot delete position with employees') {
                    return 'Невозможно удалить должность, к которой привязаны сотрудники. Сначала переназначьте сотрудников на другую должность.'
                  } else if (message === 'Position not found' || status === 404) {
                    return 'Должность не найдена. Возможно, она уже была удалена. Попробуйте обновить страницу.'
                  } else if (status === 403) {
                    return 'У вас нет прав для удаления этой должности.'
                  } else if (status === 500) {
                    return 'Произошла ошибка на сервере. Попробуйте позже.'
                  } else {
                    return message || 'Произошла неизвестная ошибка при удалении'
                  }
                })()}
              </span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            Отменить
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDelete}
            disabled={isLoading}
            className="min-w-[100px]"
          >
            {isLoading ? (
              <>
                <LoadingSpinner className="mr-2 h-4 w-4" />
                Удаление...
              </>
            ) : employeeCount > 0 ? (
              'Нельзя удалить'
            ) : (
              'Удалить'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}